/**
 * FormRenderer — renderiza um formulario a partir de {campos, config}.
 *
 * Responsabilidades (Single Responsibility): controlar o estado das
 * respostas, avaliar a logica condicional (mostra/oculta campos via
 * `condicoes.js`), validar no cliente (obrigatorio + tipo) e chamar
 * `onSubmit(dados, antispam)`. NAO sabe nada sobre transporte (HTTP) — quem chama
 * decide o que fazer com `dados` (POST publico, ou no-op no preview).
 *
 * Usado em DOIS lugares:
 *   - Preview ao vivo do builder (Editor) — `readOnly` desabilita o envio,
 *     mas os inputs continuam interativos para o gestor testar a logica
 *     condicional.
 *   - Pagina publica (/f/:slug) — envio real via `onSubmit`.
 *
 * Endereco (ADR-0071): com `buscarCep(cep8)` — Promise<endereco | null>,
 * rejeicao = busca indisponivel —, o campo mapeado a CEP busca ao completar
 * 8 digitos e preenche os campos deste formulario mapeados a logradouro,
 * bairro, cidade e UF (`preenchimentoCep.js`). O transporte continua fora
 * daqui: a pagina publica passa `buscarCepPublico(slug)`, o preview do
 * builder passa `buscarCep(request)`. Sem a prop, o CEP e so um campo.
 *
 * Estilo: usa classes canonicas (.input/.select/.textarea/.btn/.input-label
 * /.input-help) para herdar o tema (tokens). A pagina publica injeta a cor
 * do formulario via `themeColor` (vira --accent local no wrapper), entao o
 * botao primario e os focos respeitam a cor escolhida pelo gestor.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ACCEPT_ARQUIVO_FORMULARIO,
  DICA_ARQUIVO_FORMULARIO,
  MAX_ARQUIVOS_POR_CAMPO,
  TIPO_CAMPO_MAP,
  erroDoArquivoDoFormulario,
  nomeExternoDoCampo,
  tipoSemInput,
} from "../../services/formularios";
import {
  AVISOS_DA_BUSCA_DE_CEP,
  ESTADO_BUSCA_CEP,
  UFS,
  normalizarUf,
} from "../../utils/endereco";
import { camposVisiveis, chaveDoCampo } from "./condicoes";
import { varsDoDesign } from "./designConfig";
import { erroDeLimiteNoEnvio } from "./limitesCampo";
import {
  AJUDA_VALOR_ESTIMADO,
  campoDeValorEstimado,
  erroDoValorEstimado,
  valorEstimadoNoBlur,
} from "./valorEstimado";
import {
  campoDoDestino,
  cepCompleto,
  erroDoCampoDeEndereco,
  podeBuscarCep,
  valoresDaBuscaDeCep,
} from "./preenchimentoCep";
import { RenderScope } from "./styles";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const {
  OCIOSA: BUSCA_OCIOSA,
  BUSCANDO,
  NAO_ENCONTRADO: CEP_NAO_ENCONTRADO,
  INDISPONIVEL: BUSCA_INDISPONIVEL,
} = ESTADO_BUSCA_CEP;

// Textos da busca em `utils/endereco`, os mesmos do EnderecoFields. O campo
// de CEP sempre tem a regiao de aviso (vazia quando nao ha o que dizer), para
// o leitor de tela anunciar quando o texto aparecer.
const SEM_AVISO = { texto: "", tom: undefined };

function semChaves(objeto, chaves) {
  if (!chaves.some((chave) => objeto[chave])) return objeto;
  const resto = { ...objeto };
  chaves.forEach((chave) => {
    delete resto[chave];
  });
  return resto;
}

// Valor inicial neutro por tipo (arrays para multi-valor, "" para o resto).
function valorInicial(campo) {
  if (campo.tipo === "MULTI_SELECT" || campo.tipo === "CHECKBOX") return [];
  // ARQUIVO (FORM-05): lista de {token, nome} ja enviados pela rota de upload.
  if (campo.tipo === "ARQUIVO") return [];
  return "";
}

// O estado das respostas e' chaveado por `chaveDoCampo`: no preview do Editor
// os campos novos ainda nao tem id, e todos dividiam a chave "undefined" —
// digitar num preenchia os outros (FORM-04).
function montarEstadoInicial(campos, initialValues) {
  const base = {};
  (campos || []).forEach((c) => {
    if (tipoSemInput(c.tipo)) return;
    const chave = chaveDoCampo(c);
    if (initialValues && initialValues[chave] !== undefined) {
      base[chave] = initialValues[chave];
    } else {
      base[chave] = valorInicial(c);
    }
  });
  return base;
}

function FormRenderer({
  campos = [],
  config = {},
  themeColor,
  submitLabel = "Enviar",
  onSubmit,
  readOnly = false,
  submitting = false,
  initialValues,
  buscarCep,
  uploadArquivo,
}) {
  const [valores, setValores] = useState(() =>
    montarEstadoInicial(campos, initialValues)
  );
  const [erros, setErros] = useState({});
  const [erroGeral, setErroGeral] = useState("");
  const [buscaCep, setBuscaCep] = useState(BUSCA_OCIOSA);

  // A busca responde depois de um `await`: nesse meio-tempo a pessoa pode ter
  // apagado ou trocado o CEP. As refs guardam o CEP buscado e o ultimo
  // digitado, para descartar a resposta que ja nao vale.
  const cepBuscadoRef = useRef("");
  const cepDigitadoRef = useRef("");
  const montadoRef = useRef(false);
  // Anti-spam da pagina hospedada (FORM-49): o honeypot e' um input que so
  // robo preenche, e a duracao vai medida no relogio do proprio visitante —
  // comparar com o do servidor descartava humano com o relogio adiantado.
  const abertoEmRef = useRef(Date.now());
  const [honeypot, setHoneypot] = useState("");

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  // So busca o campo de CEP de um formulario que tem onde gravar o resultado.
  const campoCep = useMemo(
    () => (buscarCep && podeBuscarCep(campos) ? campoDoDestino(campos, "cep") : null),
    [buscarCep, campos]
  );

  const setCampo = useCallback((chave, valor) => {
    setValores((prev) => ({ ...prev, [chave]: valor }));
    setErros((prev) => semChaves(prev, [chave]));
  }, []);

  const preencherPeloCep = useCallback(
    async (cep) => {
      const aindaVale = () =>
        montadoRef.current
        && cepBuscadoRef.current === cep
        && cepCompleto(cepDigitadoRef.current) === cep;

      cepBuscadoRef.current = cep;
      setBuscaCep(BUSCANDO);
      let endereco;
      try {
        endereco = await buscarCep(cep);
      } catch {
        // Toda falha que nao e "nao encontrado" tem o mesmo desfecho para quem
        // responde: seguir a mao. O aviso diz que o problema e a busca.
        if (aindaVale()) setBuscaCep(BUSCA_INDISPONIVEL);
        return;
      }
      if (!aindaVale()) return;
      if (!endereco) {
        setBuscaCep(CEP_NAO_ENCONTRADO);
        return;
      }
      setBuscaCep(BUSCA_OCIOSA);
      const preenchidos = valoresDaBuscaDeCep(campos, endereco);
      setValores((prev) => ({ ...prev, ...preenchidos }));
      setErros((prev) => semChaves(prev, Object.keys(preenchidos)));
    },
    [buscarCep, campos]
  );

  const alterarCampo = useCallback(
    (campo, valor) => {
      setCampo(chaveDoCampo(campo), valor);
      if (!campoCep || chaveDoCampo(campo) !== chaveDoCampo(campoCep)) return;
      cepDigitadoRef.current = valor;
      const cep = cepCompleto(valor);
      if (!cep) {
        cepBuscadoRef.current = "";
        setBuscaCep(BUSCA_OCIOSA);
        return;
      }
      if (cep !== cepBuscadoRef.current) preencherPeloCep(cep);
    },
    [campoCep, setCampo, preencherPeloCep]
  );

  // Dinheiro se normaliza no BLUR, nunca a cada tecla: reescrever enquanto a
  // pessoa digita desfaz o que ela esta escrevendo (ADR-0059). "80000" vira
  // "80.000,00" so quando ela sai do campo, e ela confere o que vai mandar.
  const aoSairDoCampo = useCallback(
    (campo, valor) => {
      if (!campoDeValorEstimado(campo)) return;
      const formatado = valorEstimadoNoBlur(valor);
      if (formatado !== valor) setCampo(chaveDoCampo(campo), formatado);
    },
    [setCampo]
  );

  // Toggle de um valor numa lista (MULTI_SELECT / CHECKBOX).
  const toggleNaLista = useCallback(
    (chave, opcao) => {
      setValores((prev) => {
        const atual = Array.isArray(prev[chave]) ? prev[chave] : [];
        const existe = atual.includes(opcao);
        const proximo = existe
          ? atual.filter((v) => v !== opcao)
          : [...atual, opcao];
        return { ...prev, [chave]: proximo };
      });
      setErros((prev) => {
        if (!prev[chave]) return prev;
        const next = { ...prev };
        delete next[chave];
        return next;
      });
    },
    []
  );

  // Campos visiveis dado o estado atual (logica condicional ao vivo), pelo
  // mesmo algoritmo do servidor: valor de campo oculto nao decide o seguinte.
  const visiveis = useMemo(() => camposVisiveis(campos, valores), [campos, valores]);

  const validar = useCallback(() => {
    const novos = {};
    visiveis.forEach((campo) => {
      if (tipoSemInput(campo.tipo)) return;
      const chave = chaveDoCampo(campo);
      const valor = valores[chave];
      const vazio = Array.isArray(valor)
        ? valor.length === 0
        : String(valor ?? "").trim() === "";

      if (campo.obrigatorio && vazio) {
        novos[chave] = "Campo obrigatório.";
        return;
      }
      if (vazio) return; // opcional e vazio: nao valida tipo

      if (campo.tipo === "EMAIL" && !EMAIL_RE.test(String(valor).trim())) {
        novos[chave] = "Informe um e-mail válido.";
      } else {
        // Numero e limites de validacao do campo, com a mensagem do servidor
        // (FORM-39): antes o visitante so descobria o limite pelo 400.
        // UF e CEP invalidos o servidor recusa com a resposta INTEIRA
        // (FORM-36): apontar no campo antes poupa o visitante do erro no
        // rodape. UF por extenso passa — o servidor converte para a sigla.
        // Dinheiro tem regua propria (pt-BR): "80.000" e oitenta mil, e
        // "uns 50 mil" e frase, nao valor (FORM-03).
        const erroDoCampo = campoDeValorEstimado(campo)
          ? erroDoValorEstimado(campo, valor)
          : erroDeLimiteNoEnvio(campo, valor) || erroDoCampoDeEndereco(campo, valor);
        if (erroDoCampo) novos[chave] = erroDoCampo;
      }
    });
    setErros(novos);
    return Object.keys(novos).length === 0;
  }, [visiveis, valores]);

  // Monta o payload {campo_id: valor} apenas com campos visiveis e com
  // valor (campos ocultos pela condicao NUNCA sao enviados).
  const montarDados = useCallback(() => {
    const dados = {};
    visiveis.forEach((campo) => {
      if (tipoSemInput(campo.tipo)) return;
      const chave = chaveDoCampo(campo);
      const valor = valores[chave];
      const vazio = Array.isArray(valor)
        ? valor.length === 0
        : String(valor ?? "").trim() === "";
      if (vazio) return;
      // O servidor recebe os tokens dos arquivos, nao os nomes (FORM-05).
      dados[chave] = campo.tipo === "ARQUIVO" ? valor.map((a) => a.token) : valor;
    });
    return dados;
  }, [visiveis, valores]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (readOnly) return;
      setErroGeral("");
      if (!validar()) {
        setErroGeral("Confira os campos destacados antes de enviar.");
        return;
      }
      try {
        await onSubmit?.(montarDados(), {
          gotcha: honeypot,
          tempoMs: Date.now() - abertoEmRef.current,
        });
      } catch (err) {
        setErroGeral(err?.message || "Não foi possível enviar. Tente novamente.");
      }
    },
    [readOnly, validar, onSubmit, montarDados, honeypot]
  );

  // Cor do formulario -> --accent local (foco/botao herdam). Sem cor, usa
  // o accent global do tema. O design (config.design) entra como variaveis
  // --fb-* (espacamento, colunas, cantos, botao) lidas pelo RenderScope —
  // assim o preview do Editor e a pagina publica respondem igual.
  const wrapperStyle = useMemo(
    () => ({
      ...(themeColor ? { "--accent": themeColor } : {}),
      ...varsDoDesign(config?.design),
    }),
    [themeColor, config?.design]
  );

  const semCampos = visiveis.length === 0;

  return (
    <RenderScope
      as="form"
      className="fb-render"
      style={wrapperStyle}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="fb-render__grid">
        {visiveis.map((campo) => {
          const chave = chaveDoCampo(campo);
          return (
            <CampoRender
              key={chave}
              campo={campo}
              chave={chave}
              valor={valores[chave]}
              erro={erros[chave]}
              aviso={
                campoCep && chaveDoCampo(campoCep) === chave
                  ? AVISOS_DA_BUSCA_DE_CEP[buscaCep] || SEM_AVISO
                  : null
              }
              onChange={(v) => alterarCampo(campo, v)}
              onBlurValor={(v) => aoSairDoCampo(campo, v)}
              onToggleLista={(op) => toggleNaLista(chave, op)}
              uploadArquivo={uploadArquivo}
            />
          );
        })}
      </div>

      {erroGeral && (
        <div className="so-banner danger" role="alert" style={{ marginTop: 14 }}>
          <span>{erroGeral}</span>
        </div>
      )}

      {/* Isca anti-robo: fora do fluxo visual e da ordem de tabulacao; leitor de tela ignora. */}
      {!readOnly && !semCampos && (
        <input
          type="text"
          name="_gotcha"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          data-testid="fb-honeypot"
          style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
        />
      )}

      {!semCampos && (
        <div className="fb-render__foot">
          <button
            type="submit"
            className="btn btn--primary btn--lg"
            disabled={readOnly || submitting}
            title={readOnly ? "Pre-visualizacao — envio desabilitado" : undefined}
          >
            {submitting ? "Enviando..." : submitLabel}
          </button>
        </div>
      )}
    </RenderScope>
  );
}

// ------------------------------------------------------------
// Render de um campo individual (por tipo)
// ------------------------------------------------------------

const TIPOS_DE_TEXTO_LIVRE = ["TEXTO", "TEXTAREA"];

// Campo de texto livre mapeado a UF vira a lista das 27 (FORM-36): quem
// digitava "S.P." ou um estado com erro so descobria ao enviar. Lista do
// gestor (SELECT/RADIO) fica com as opcoes dele — o servidor aceita so elas.
function ehUfDeTextoLivre(campo) {
  return TIPOS_DE_TEXTO_LIVRE.includes(campo.tipo) && nomeExternoDoCampo(campo) === "estado";
}

/**
 * Campo ARQUIVO (FORM-05): cada arquivo escolhido sobe na hora pela rota de
 * upload e vira {token, nome} no valor; o envio do formulario cita os tokens.
 * Sem `uploadArquivo` (preview do builder) o controle fica desabilitado.
 */
function ArquivoInput({ inputId, valor, erro, obrig, onChange, uploadArquivo }) {
  const [enviando, setEnviando] = useState(false);
  const [erroUpload, setErroUpload] = useState("");
  const lista = Array.isArray(valor) ? valor : [];
  const lotado = lista.length >= MAX_ARQUIVOS_POR_CAMPO;

  const escolher = async (event) => {
    const escolhidos = Array.from(event.target.files || []);
    event.target.value = "";
    if (!escolhidos.length || !uploadArquivo) return;
    setErroUpload("");
    if (lista.length + escolhidos.length > MAX_ARQUIVOS_POR_CAMPO) {
      setErroUpload(`Envie no máximo ${MAX_ARQUIVOS_POR_CAMPO} arquivos.`);
      return;
    }
    const invalido = escolhidos.map(erroDoArquivoDoFormulario).find(Boolean);
    if (invalido) {
      setErroUpload(invalido);
      return;
    }
    setEnviando(true);
    try {
      const enviados = [];
      for (const arquivo of escolhidos) {
        // Sequencial de proposito: o limite por IP e' baixo e a ordem importa.
        // eslint-disable-next-line no-await-in-loop
        const res = await uploadArquivo(arquivo);
        enviados.push({ token: res.token, nome: res.nome || arquivo.name });
      }
      onChange([...lista, ...enviados]);
    } catch (err) {
      setErroUpload(err?.message || "Não foi possível enviar o arquivo.");
    } finally {
      setEnviando(false);
    }
  };

  const remover = (token) => onChange(lista.filter((a) => a.token !== token));
  const dicaId = `${inputId}-dica`;

  return (
    <div className="fb-arquivo">
      <input
        id={inputId}
        className="input"
        type="file"
        multiple
        accept={ACCEPT_ARQUIVO_FORMULARIO}
        onChange={escolher}
        disabled={!uploadArquivo || enviando || lotado}
        aria-invalid={Boolean(erro || erroUpload)}
        aria-required={obrig}
        aria-describedby={dicaId}
        style={{ height: "auto", padding: "8px 12px" }}
      />
      <span id={dicaId} className="input-help">
        {enviando ? "Enviando arquivo…" : DICA_ARQUIVO_FORMULARIO}
      </span>
      {lista.length > 0 && (
        <ul className="fb-arquivo__lista" aria-label="Arquivos enviados">
          {lista.map((a) => (
            <li key={a.token}>
              <span>{a.nome}</span>{" "}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => remover(a.token)}
                aria-label={`Remover ${a.nome}`}
              >
                Remover
              </button>
            </li>
          ))}
        </ul>
      )}
      {erroUpload && (
        <span className="fb-field__error" role="alert">
          {erroUpload}
        </span>
      )}
    </div>
  );
}

function CampoRender({
  campo, chave, valor, erro, aviso, onChange, onBlurValor, onToggleLista, uploadArquivo,
}) {
  const meta = TIPO_CAMPO_MAP[campo.tipo];
  const larguraFull = campo.largura !== "HALF";
  const className = `fb-field${larguraFull ? " fb-field--full" : ""}`;
  const inputId = `campo-${chave}`;
  // Aviso da busca de CEP: a regiao `status` existe desde o inicio (vazia)
  // para o leitor de tela anunciar quando o texto aparecer.
  const avisoId = aviso ? `${inputId}-busca-cep` : undefined;

  // SECAO: separador/titulo, sem input.
  if (campo.tipo === "SECAO") {
    return (
      <div className={`${className} fb-field--secao`}>
        <h3 className="fb-secao__title">{campo.label || "Seção"}</h3>
        {campo.ajuda && <p className="fb-secao__sub">{campo.ajuda}</p>}
      </div>
    );
  }

  const opcoes = Array.isArray(campo.opcoes) ? campo.opcoes : [];
  const obrig = Boolean(campo.obrigatorio);
  const placeholder = campo.placeholder || "";
  // Campo de dinheiro: texto, mesmo quando o tipo e NUMERO. `type=number`
  // rejeita "80.000" e "R$ 80.000,00" no proprio navegador (o campo fica
  // vazio), que e a digitacao pt-BR que o servidor passou a aceitar.
  const ehDinheiro = campoDeValorEstimado(campo);

  let controle = null;

  if (ehUfDeTextoLivre(campo)) {
    const sigla = normalizarUf(valor);
    // UF antiga fora da lista (valor inicial, resposta anterior): continua
    // visivel em vez de sumir do select — a validacao aponta o erro.
    const foraDaLista = sigla === null;
    controle = (
      <select
        id={inputId}
        className="select"
        value={foraDaLista ? valor : sigla}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(erro)}
        aria-required={obrig}
        aria-describedby={avisoId}
      >
        <option value="">{placeholder || "Selecione..."}</option>
        {foraDaLista && <option value={valor}>{`${valor} (inválida)`}</option>}
        {UFS.map((uf) => (
          <option key={uf.sigla} value={uf.sigla}>
            {`${uf.sigla} — ${uf.nome}`}
          </option>
        ))}
      </select>
    );
  } else if (campo.tipo === "TEXTAREA") {
    controle = (
      <textarea
        id={inputId}
        className="textarea"
        value={valor ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(erro)}
        aria-required={obrig}
        aria-describedby={avisoId}
        rows={4}
      />
    );
  } else if (campo.tipo === "SELECT") {
    controle = (
      <select
        id={inputId}
        className="select"
        value={valor ?? ""}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(erro)}
        aria-required={obrig}
        aria-describedby={avisoId}
      >
        <option value="">{placeholder || "Selecione..."}</option>
        {opcoes.map((op, i) => (
          <option key={`${op}-${i}`} value={op}>
            {op}
          </option>
        ))}
      </select>
    );
  } else if (campo.tipo === "RADIO") {
    controle = (
      <div className="fb-choices" role="radiogroup" aria-label={campo.label}>
        {opcoes.map((op, i) => (
          <label key={`${op}-${i}`} className="fb-choice">
            <input
              type="radio"
              name={inputId}
              value={op}
              checked={valor === op}
              onChange={() => onChange(op)}
            />
            <span>{op}</span>
          </label>
        ))}
      </div>
    );
  } else if (campo.tipo === "CHECKBOX" || campo.tipo === "MULTI_SELECT") {
    const arr = Array.isArray(valor) ? valor : [];
    controle = (
      <div className="fb-choices" role="group" aria-label={campo.label}>
        {opcoes.map((op, i) => (
          <label key={`${op}-${i}`} className="fb-choice">
            <input
              type="checkbox"
              value={op}
              checked={arr.includes(op)}
              onChange={() => onToggleLista(op)}
            />
            <span>{op}</span>
          </label>
        ))}
      </div>
    );
  } else {
    // Inputs de texto/linha unica: TEXTO, EMAIL, TELEFONE, NUMERO, DATA,
    // CPF_CNPJ, ARQUIVO.
    const tipoHtml = ehDinheiro ? "text" : ({
      EMAIL: "email",
      TELEFONE: "tel",
      NUMERO: "number",
      DATA: "date",
      ARQUIVO: "file",
    }[campo.tipo] || "text");

    if (campo.tipo === "ARQUIVO") {
      controle = (
        <ArquivoInput
          inputId={inputId}
          valor={valor}
          erro={erro}
          obrig={obrig}
          onChange={onChange}
          uploadArquivo={uploadArquivo}
        />
      );
    } else {
      controle = (
        <input
          id={inputId}
          className="input"
          type={tipoHtml}
          value={valor ?? ""}
          placeholder={placeholder}
          inputMode={campo.tipo === "NUMERO" || ehDinheiro ? "decimal" : undefined}
          onChange={(e) => onChange(e.target.value)}
          onBlur={ehDinheiro ? (e) => onBlurValor?.(e.target.value) : undefined}
          aria-invalid={Boolean(erro)}
          aria-required={obrig}
          aria-describedby={avisoId}
        />
      );
    }
  }

  // RADIO/CHECKBOX/MULTI_SELECT usam <label> proprio por opcao; o titulo do
  // grupo nao deve ser um <label for> (acessibilidade). Os demais usam label.
  const tituloComoLabel = !["RADIO", "CHECKBOX", "MULTI_SELECT"].includes(
    campo.tipo
  );
  const Titulo = tituloComoLabel ? "label" : "span";

  return (
    <div className={className}>
      <Titulo
        className="input-label fb-field__label"
        htmlFor={tituloComoLabel ? inputId : undefined}
      >
        {campo.label || meta?.label || "Campo"}
        {obrig && (
          <span className="fb-req" aria-hidden="true">
            {" *"}
          </span>
        )}
      </Titulo>
      {controle}
      {aviso && (
        <span
          id={avisoId}
          role="status"
          className={`input-help${aviso.tom ? ` fb-field__aviso--${aviso.tom}` : ""}`}
        >
          {aviso.texto}
        </span>
      )}
      {ehDinheiro && !campo.ajuda && (
        <span className="input-help">{AJUDA_VALOR_ESTIMADO}</span>
      )}
      {campo.ajuda && <span className="input-help">{campo.ajuda}</span>}
      {erro && (
        <span className="fb-field__error" role="alert">
          {erro}
        </span>
      )}
    </div>
  );
}

export default FormRenderer;
